/**
 * Project setup redesign — Track C (structure + readiness): namespace `projectSetup`.
 *
 * Top-level namespaces owned by this track only. `checklistNl` is typed against
 * `checklistEn`, so a key missing in Dutch fails typecheck exactly as it does in
 * the main dictionary.
 *
 * `projectSetup.checklist.items.<copyId>` is keyed by the checklist item's
 * `copyId` (src/lib/project-setup/checklist.ts): the readiness check id with
 * `.`/`-` as `_`, `step_<setupStep>`, `support_code` and `members`.
 */
export const checklistEn = {
  projectSetup: {
    nav: {
      /** The rail group for the pages a project is set up in. */
      groupSetup: "Set up",
    },
    wizardProgress: {
      /** A wizard step whose total is not settled yet (the repository wizard's first steps). */
      stepOnly: "Step {step}",
    },
    checklist: {
      title: "Setup & status",
      subtitle: "Everything this project needs, in one list.",
      verdict: {
        ready: "Project ready",
        needsSetup: "Setup incomplete",
        needsAttention: "Needs attention",
      },
      verdictBody: {
        ready: "Everything this project needs is set up and working.",
        readyAttentionOne: "Everything required works. {count} other item needs attention.",
        readyAttentionOther: "Everything required works. {count} other items need attention.",
        needsAttention: "Everything required is set up, but not all of it is working or tested yet.",
        brokenOne: "{count} connection this project uses can't be reached right now.",
        brokenOther: "{count} connections this project uses can't be reached right now.",
      },
      progress: "Setup: {done} of {total} required steps complete",
      progressUnproved: "Setup: {setUp} of {total} required steps set up, {done} proved to work",
      progressNone: "This project has no required setup steps.",
      nextLabel: "Next step",
      noPermission: "Someone who manages this project's settings needs to do this.",
      groups: {
        required: "Required",
        recommended: "Recommended",
        optional: "More options",
        optionalHint: "Not needed to get started. Add them when they help.",
        showOptional: "More options ({count})",
        hideOptional: "Hide more options",
      },
      state: {
        done: "Done",
        todoRequired: "Not set up",
        todoRecommended: "Recommended",
        todoOptional: "Optional",
        untested: "Not tested yet",
        attention: "Needs attention",
      },
      actions: {
        review: "Review",
      },
      test: {
        button: "Test everything",
        running: "Testing {current} of {total}…",
        none: "There are no connections to test yet.",
        doneAll: "All {count} connections work.",
        doneSome: "{failed} of {count} connections need attention.",
        pass: "Working",
        fail: "Needs attention",
        failed: "The test could not be run. Try again in a moment.",
      },
      guide: {
        title: "Let's get this project ready",
        intro: "Five questions, in order. You can skip this and come back to the list below any time.",
        skip: "Skip for now",
        start: "Start",
        reopen: "Show guided setup",
        progressLabel: "Guided setup",
        themes: {
          work: "Connect where work comes from",
          code: "Connect the codebase",
          communication: "Set up communication",
          testing: "Verify testing",
          approvals: "Review approvals",
        },
        state: {
          done: "Done",
          open: "To do",
          untested: "Not tested yet",
          optional: "Optional",
          approvalsInPlace: "Company rules apply",
        },
      },
      details: {
        summary: "Details: what this project uses and what FixControl can do with it",
      },
      items: {
        support_intake_source: {
          title: "Where customer questions come in",
          why: "Connect a mailbox to receive customer email in this project.",
          whyUntested: "It is set up, but nothing has proved it works yet. Test it on Connections, or send an email to it and check that it arrives.",
          action: "Connect mailbox",
        },
        support_approval_policy: {
          title: "Approval of replies to customers",
          why: "No reply reaches a customer unless a person sends or approves it. Who may approve is set here; whether a reply first needs an approval is set under Customer communication.",
          whyAttention: "Nobody can approve customer replies yet: approving needs an active administrator or manager, and your organization has none. Open Approvals & rules for details.",
          whyMode: {
            off: "Replies need no approval now: whoever writes one sends it. To have AI replies checked by a person first, turn it on under Approvals & rules.",
            ai_drafts: "Replies from the AI wait for approval by an administrator or manager. A reply a person writes goes with one click.",
            all: "Every reply waits for approval by an administrator or manager before it goes out.",
          },
          action: "Review approvals",
        },
        support_knowledge_source: {
          title: "Knowledge for answers",
          why: "Add documentation so FixControl can draft better answers.",
          action: "Add knowledge",
        },
        support_reply_destination: {
          title: "Replying to customers",
          why: "Let approved replies go out through your mailbox.",
          action: "Set up replies",
        },
        support_send_health: {
          title: "Replies are delivered",
          why: "Check that approved replies actually reach the customer.",
          action: "Check connection",
        },
        engineering_codebase: {
          title: "The code",
          why: "Connect the repository FixControl reads and proposes changes for.",
          action: "Connect repository",
        },
        engineering_code_access: {
          title: "Access to the code",
          why: "FixControl needs read access to the repository to work on it.",
          action: "Connect repository",
        },
        engineering_sandbox: {
          title: "A safe place to try changes",
          why: "Changes are tried in an isolated copy before anyone approves them.",
          action: "Open environments",
        },
        engineering_environment: {
          title: "The application starts",
          why: "Prove once that FixControl can start your application, so every change can be tested.",
          action: "Open environments",
        },
        engineering_approval_route: {
          title: "Who approves changes",
          why: "Decide who signs off on a proposed change before it goes anywhere.",
          whyAttention: "Nobody can approve changes yet: approving needs an active administrator or manager, and your organization has none. Open Approvals & rules for details.",
          action: "Review approvals",
        },
        engineering_pr_provider: {
          title: "Deliver approved changes",
          why: "Approved changes arrive as a pull request in your repository.",
          action: "Connect repository",
        },
        deployment_binding: {
          title: "Your deployments",
          why: "Connect the system that rolls out your application.",
          action: "Open connections",
        },
        deployment_observe_health: {
          title: "Deployment status is visible",
          why: "FixControl reads whether each rollout is healthy.",
          action: "Open connections",
        },
        deployment_approval_route: {
          title: "Who approves rollouts",
          why: "Decide who signs off before a rollout continues.",
          action: "Review approvals",
        },
        deployment_action_capability: {
          title: "Continue rollouts from FixControl",
          why: "Let an approved rollout be continued from here.",
          action: "Open connections",
        },
        security_codebase: {
          title: "The code to check",
          why: "Connect the repository whose security you want to follow.",
          action: "Connect repository",
        },
        security_scanner_capability: {
          title: "Security checks can run",
          why: "At least one security check has to have run on this code.",
          action: "Open code",
        },
        security_scan_status: {
          title: "A recent security check",
          why: "Run a security check so there is a current picture.",
          action: "Open code",
        },
        security_remediation_route: {
          title: "Deliver security fixes",
          why: "Approved fixes arrive as a pull request in your repository.",
          action: "Connect repository",
        },
        knowledge_source: {
          title: "Knowledge sources",
          why: "Add the documentation this project answers from.",
          action: "Add knowledge",
        },
        knowledge_indexing: {
          title: "Knowledge is ready to use",
          why: "Documents are processed before they can be used in answers.",
          action: "Open knowledge",
        },
        knowledge_scope: {
          title: "What is shared publicly",
          why: "Choose which knowledge your website chat may use.",
          action: "Open chat",
        },
        knowledge_intake_channel: {
          title: "Where questions come in",
          why: "Connect a channel where people ask questions.",
          action: "Open connections",
        },
        custom_any_context: {
          title: "Something to work with",
          why: "Connect at least one system, repository or knowledge source.",
          action: "Open connections",
        },
        step_workSource: {
          title: "Where work comes in",
          why: "Connect the support software or mailbox where requests arrive.",
          action: "Connect a work source",
        },
        step_codebase: {
          title: "The code",
          why: "Connect the repository this project works on.",
          action: "Connect repository",
        },
        step_knowledge: {
          title: "Knowledge",
          why: "Add documentation FixControl can draw on.",
          action: "Add knowledge",
        },
        step_deployment: {
          title: "Your deployments",
          why: "Connect the system that rolls out your application.",
          action: "Open connections",
        },
        step_delivery: {
          title: "Deliver approved changes",
          why: "Approved changes arrive as a pull request in your repository.",
          action: "Connect repository",
        },
        step_approvals: {
          title: "Where approval requests go",
          why: "Receive approval requests in Slack, Teams or email, not only in FixControl.",
          action: "Review approvals",
        },
        step_notifications: {
          title: "Progress updates in chat",
          why: "Post progress updates to a Slack or Teams channel.",
          action: "Connect chat",
        },
        support_code: {
          title: "Code, to fix what customers report",
          why: "Connect a repository if you also want FixControl to propose fixes.",
          action: "Connect repository",
        },
        members: {
          title: "Members",
          why: "Add the colleagues who work on this project.",
          action: "Add member",
        },
        communication_style: {
          title: "Reply style",
          why: "Give replies to customers a consistent greeting, closing and signature.",
          action: "Set up reply style",
        },
        communication_templates: {
          title: "Reply templates",
          why: "Optional: reusable answers for common replies.",
          action: "Add a template",
        },
      },
    },
    /** Code on a support project: reachable, and explained. */
    supportCode: {
      title: "This project answers customer questions.",
      body: "Connect the code if you also want FixControl to propose fixes for the problems customers report.",
      action: "Connect repository",
    },
    /** A code-change page (test environments, patches, work sessions) opened
     *  on a support project. */
    supportUnavailable: {
      title: "There is no code work on this project.",
      body: "This project answers customer questions. To have FixControl propose code changes, connect a repository first.",
      action: "Go to Code",
    },
  },
};

export const checklistNl: typeof checklistEn = {
  projectSetup: {
    nav: {
      groupSetup: "Instellen",
    },
    wizardProgress: {
      stepOnly: "Stap {step}",
    },
    checklist: {
      title: "Instellen & status",
      subtitle: "Alles wat dit project nodig heeft, in één lijst.",
      verdict: {
        ready: "Project klaar voor gebruik",
        needsSetup: "Instellen nog niet af",
        needsAttention: "Vraagt aandacht",
      },
      verdictBody: {
        ready: "Alles wat dit project nodig heeft is ingesteld en werkt.",
        readyAttentionOne: "Alles wat nodig is werkt. {count} ander onderdeel vraagt aandacht.",
        readyAttentionOther: "Alles wat nodig is werkt. {count} andere onderdelen vragen aandacht.",
        needsAttention: "Alles wat nodig is, is ingesteld, maar nog niet alles werkt of is getest.",
        brokenOne: "{count} verbinding die dit project gebruikt is nu niet bereikbaar.",
        brokenOther: "{count} verbindingen die dit project gebruikt zijn nu niet bereikbaar.",
      },
      progress: "Instellen: {done} van {total} verplichte stappen klaar",
      progressUnproved: "Instellen: {setUp} van {total} verplichte stappen ingesteld, {done} bewezen werkend",
      progressNone: "Dit project heeft geen verplichte stappen.",
      nextLabel: "Volgende stap",
      noPermission: "Iemand die de instellingen van dit project beheert moet dit doen.",
      groups: {
        required: "Verplicht",
        recommended: "Aanbevolen",
        optional: "Meer opties",
        optionalHint: "Niet nodig om te beginnen. Voeg ze toe als ze helpen.",
        showOptional: "Meer opties ({count})",
        hideOptional: "Meer opties verbergen",
      },
      state: {
        done: "Klaar",
        todoRequired: "Nog niet ingesteld",
        todoRecommended: "Aanbevolen",
        todoOptional: "Optioneel",
        untested: "Nog niet getest",
        attention: "Vraagt aandacht",
      },
      actions: {
        review: "Bekijken",
      },
      test: {
        button: "Alles testen",
        running: "Bezig met testen: {current} van {total}…",
        none: "Er zijn nog geen verbindingen om te testen.",
        doneAll: "Alle {count} verbindingen werken.",
        doneSome: "{failed} van {count} verbindingen vragen aandacht.",
        pass: "Werkt",
        fail: "Vraagt aandacht",
        failed: "De test kon niet worden uitgevoerd. Probeer het zo nog eens.",
      },
      guide: {
        title: "Laten we dit project klaarmaken",
        intro: "Vijf vragen, op volgorde. Je kunt dit overslaan en later altijd verder met de lijst hieronder.",
        skip: "Nu overslaan",
        start: "Beginnen",
        reopen: "Begeleid instellen tonen",
        progressLabel: "Begeleid instellen",
        themes: {
          work: "Koppel waar het werk vandaan komt",
          code: "Koppel de code",
          communication: "Stel de communicatie in",
          testing: "Controleer het testen",
          approvals: "Bekijk de goedkeuringen",
        },
        state: {
          done: "Klaar",
          open: "Te doen",
          untested: "Nog niet getest",
          optional: "Optioneel",
          approvalsInPlace: "Bedrijfsregels gelden",
        },
      },
      details: {
        summary: "Details: wat dit project gebruikt en wat FixControl ermee kan",
      },
      items: {
        support_intake_source: {
          title: "Waar klantvragen binnenkomen",
          why: "Koppel een mailbox om klantmail in dit project te ontvangen.",
          whyUntested: "Het is ingesteld, maar nog niets bewijst dat het werkt. Test het bij Verbindingen, of stuur er een e-mail naartoe en kijk of die aankomt.",
          action: "Mailbox koppelen",
        },
        support_approval_policy: {
          title: "Goedkeuring van antwoorden aan klanten",
          why: "Geen antwoord gaat naar een klant zonder dat een mens het verstuurt of goedkeurt. Wie mag goedkeuren staat hier; of een antwoord eerst goedgekeurd moet worden, stel je in bij Klantcommunicatie.",
          whyAttention: "Nog niemand kan antwoorden aan klanten goedkeuren: daarvoor is een actieve beheerder of manager nodig, en je organisatie heeft er geen. Open Goedkeuringen & regels voor details.",
          whyMode: {
            off: "Antwoorden hoeven nu niet goedgekeurd te worden: wie een antwoord schrijft, verstuurt het zelf. Wil je dat AI-antwoorden eerst langs een mens gaan, zet het dan aan bij Goedkeuringen & regels.",
            ai_drafts: "Antwoorden van de AI wachten op goedkeuring door een beheerder of manager. Wat een mens zelf schrijft, gaat met één klik.",
            all: "Elk antwoord wacht op goedkeuring door een beheerder of manager voordat het verstuurd wordt.",
          },
          action: "Goedkeuringen bekijken",
        },
        support_knowledge_source: {
          title: "Kennis voor antwoorden",
          why: "Voeg documentatie toe, dan schrijft FixControl betere antwoorden.",
          action: "Kennis toevoegen",
        },
        support_reply_destination: {
          title: "Antwoorden aan klanten",
          why: "Laat goedgekeurde antwoorden via je mailbox verstuurd worden.",
          action: "Antwoorden instellen",
        },
        support_send_health: {
          title: "Antwoorden komen aan",
          why: "Controleer of goedgekeurde antwoorden echt bij de klant aankomen.",
          action: "Verbinding controleren",
        },
        engineering_codebase: {
          title: "De code",
          why: "Koppel de repository die FixControl leest en waarvoor het wijzigingen voorstelt.",
          action: "Repository koppelen",
        },
        engineering_code_access: {
          title: "Toegang tot de code",
          why: "FixControl heeft leestoegang tot de repository nodig om eraan te werken.",
          action: "Repository koppelen",
        },
        engineering_sandbox: {
          title: "Een veilige plek om wijzigingen te proberen",
          why: "Wijzigingen worden in een afgeschermde kopie geprobeerd voordat iemand ze goedkeurt.",
          action: "Omgevingen openen",
        },
        engineering_environment: {
          title: "De applicatie start",
          why: "Laat één keer zien dat FixControl je applicatie kan starten, zodat elke wijziging getest kan worden.",
          action: "Omgevingen openen",
        },
        engineering_approval_route: {
          title: "Wie wijzigingen goedkeurt",
          why: "Bepaal wie een voorgestelde wijziging aftekent voordat die ergens heen gaat.",
          whyAttention: "Nog niemand kan wijzigingen goedkeuren: daarvoor is een actieve beheerder of manager nodig, en je organisatie heeft er geen. Open Goedkeuringen & regels voor details.",
          action: "Goedkeuringen bekijken",
        },
        engineering_pr_provider: {
          title: "Goedgekeurde wijzigingen afleveren",
          why: "Goedgekeurde wijzigingen komen als pull request in je repository.",
          action: "Repository koppelen",
        },
        deployment_binding: {
          title: "Je deployments",
          why: "Koppel het systeem dat je applicatie uitrolt.",
          action: "Verbindingen openen",
        },
        deployment_observe_health: {
          title: "De status van uitrol is zichtbaar",
          why: "FixControl leest of elke uitrol gezond is.",
          action: "Verbindingen openen",
        },
        deployment_approval_route: {
          title: "Wie een uitrol goedkeurt",
          why: "Bepaal wie aftekent voordat een uitrol verdergaat.",
          action: "Goedkeuringen bekijken",
        },
        deployment_action_capability: {
          title: "Een uitrol vanuit FixControl voortzetten",
          why: "Laat een goedgekeurde uitrol vanuit hier verdergaan.",
          action: "Verbindingen openen",
        },
        security_codebase: {
          title: "De code om te controleren",
          why: "Koppel de repository waarvan je de beveiliging wilt volgen.",
          action: "Repository koppelen",
        },
        security_scanner_capability: {
          title: "Beveiligingscontroles kunnen draaien",
          why: "Er moet minstens één beveiligingscontrole op deze code gedraaid hebben.",
          action: "Code openen",
        },
        security_scan_status: {
          title: "Een recente beveiligingscontrole",
          why: "Draai een beveiligingscontrole, dan is er een actueel beeld.",
          action: "Code openen",
        },
        security_remediation_route: {
          title: "Beveiligingsfixes afleveren",
          why: "Goedgekeurde fixes komen als pull request in je repository.",
          action: "Repository koppelen",
        },
        knowledge_source: {
          title: "Kennisbronnen",
          why: "Voeg de documentatie toe waaruit dit project antwoordt.",
          action: "Kennis toevoegen",
        },
        knowledge_indexing: {
          title: "Kennis is klaar voor gebruik",
          why: "Documenten worden eerst verwerkt voordat ze in antwoorden gebruikt kunnen worden.",
          action: "Kennis openen",
        },
        knowledge_scope: {
          title: "Wat openbaar gedeeld wordt",
          why: "Kies welke kennis je websitechat mag gebruiken.",
          action: "Chat openen",
        },
        knowledge_intake_channel: {
          title: "Waar vragen binnenkomen",
          why: "Koppel een kanaal waar mensen vragen stellen.",
          action: "Verbindingen openen",
        },
        custom_any_context: {
          title: "Iets om mee te werken",
          why: "Koppel minstens één systeem, repository of kennisbron.",
          action: "Verbindingen openen",
        },
        step_workSource: {
          title: "Waar het werk binnenkomt",
          why: "Koppel de supportsoftware of mailbox waar verzoeken binnenkomen.",
          action: "Werkbron koppelen",
        },
        step_codebase: {
          title: "De code",
          why: "Koppel de repository waar dit project aan werkt.",
          action: "Repository koppelen",
        },
        step_knowledge: {
          title: "Kennis",
          why: "Voeg documentatie toe waar FixControl uit kan putten.",
          action: "Kennis toevoegen",
        },
        step_deployment: {
          title: "Je deployments",
          why: "Koppel het systeem dat je applicatie uitrolt.",
          action: "Verbindingen openen",
        },
        step_delivery: {
          title: "Goedgekeurde wijzigingen afleveren",
          why: "Goedgekeurde wijzigingen komen als pull request in je repository.",
          action: "Repository koppelen",
        },
        step_approvals: {
          title: "Waar goedkeuringsverzoeken heen gaan",
          why: "Ontvang goedkeuringsverzoeken in Slack, Teams of e-mail, niet alleen in FixControl.",
          action: "Goedkeuringen bekijken",
        },
        step_notifications: {
          title: "Voortgang in de chat",
          why: "Stuur voortgangsupdates naar een Slack- of Teams-kanaal.",
          action: "Chat koppelen",
        },
        support_code: {
          title: "Code, om op te lossen wat klanten melden",
          why: "Koppel een repository als je ook wilt dat FixControl oplossingen voorstelt.",
          action: "Repository koppelen",
        },
        members: {
          title: "Leden",
          why: "Voeg de collega's toe die aan dit project werken.",
          action: "Lid toevoegen",
        },
        communication_style: {
          title: "Antwoordstijl",
          why: "Geef antwoorden aan klanten een vaste aanhef, afsluiting en handtekening.",
          action: "Antwoordstijl instellen",
        },
        communication_templates: {
          title: "Antwoordtemplates",
          why: "Optioneel: herbruikbare antwoorden voor veelvoorkomende vragen.",
          action: "Template toevoegen",
        },
      },
    },
    supportCode: {
      title: "Dit project beantwoordt klantvragen.",
      body: "Koppel de code als je ook wilt dat FixControl oplossingen voorstelt voor de problemen die klanten melden.",
      action: "Repository koppelen",
    },
    supportUnavailable: {
      title: "Op dit project is er geen werk aan code.",
      body: "Dit project beantwoordt klantvragen. Wil je dat FixControl codewijzigingen voorstelt, koppel dan eerst een repository.",
      action: "Naar Code",
    },
  },
};
